import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

import {
  createApplication,
  updateApplication,
  submitApplication,
  uploadCertification,
  uploadIdDocument,
  sendEmailVerification,
  checkEmailVerified,
} from '../../services/professionalApplicationService';
import { CATEGORIES, DAYS } from '../../services/professionalService';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Compte', icon: 'UserPlus' },
  { id: 2, label: 'Identité', icon: 'ShieldCheck' },
  { id: 3, label: 'Profil', icon: 'User' },
  { id: 4, label: 'Catégorie', icon: 'Briefcase' },
];

const COMMUNES_ABIDJAN = [
  'Abobo', 'Adjamé', 'Attécoubé', 'Cocody', 'Koumassi',
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon',
];

const LANGUAGES_OPTIONS = ['Français', 'Anglais', 'Dioula', 'Baoulé', 'Bété', 'Arabe'];

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center mb-10">
    {STEPS?.map((step, idx) => (
      <React.Fragment key={step?.id}>
        <div className="flex flex-col items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep > step?.id
                ? 'bg-brand-earth text-white shadow-ios-medium'
                : currentStep === step?.id
                ? 'bg-brand-gold text-white shadow-ios-medium ring-4 ring-brand-gold/20'
                : 'bg-surface border-2 border-border text-text-secondary'
            }`}
          >
            {currentStep > step?.id ? (
              <Icon name="Check" size={18} strokeWidth={2.5} />
            ) : (
              <Icon name={step?.icon} size={17} strokeWidth={2} />
            )}
          </div>
          <span
            className={`mt-1.5 text-[11px] font-system font-body-medium tracking-tight ${
              currentStep === step?.id ? 'text-brand-earth' : 'text-text-secondary'
            }`}
          >
            {step?.label}
          </span>
        </div>
        {idx < STEPS?.length - 1 && (
          <div
            className={`flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all duration-300 ${
              currentStep > step?.id ? 'bg-brand-earth' : 'bg-border'
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ─── Field Components ─────────────────────────────────────────────────────────

const Field = ({ label, required = false, error = null, children }) => (
  <div className="space-y-1.5">
    <label className="block text-ios-callout font-system font-body-medium text-text-primary">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && (
      <p className="text-[12px] text-red-500 font-system font-body-normal flex items-center gap-1">
        <Icon name="AlertCircle" size={12} /> {error}
      </p>
    )}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', ...rest }) => (
  <input
    type={type}
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    className="w-full px-4 py-3 rounded-xl border border-border bg-white text-ios-body font-system font-body-normal text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all duration-200"
    {...rest}
  />
);

const TextArea = ({ value, onChange, placeholder, rows = 4 }) => (
  <textarea
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-4 py-3 rounded-xl border border-border bg-white text-ios-body font-system font-body-normal text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all duration-200 resize-none"
  />
);

const SelectInput = ({ value, onChange, options, placeholder }) => (
  <select
    value={value || ''}
    onChange={onChange}
    className="w-full px-4 py-3 rounded-xl border border-border bg-white text-ios-body font-system font-body-normal text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all duration-200"
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options?.map((opt) => (
      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
    ))}
  </select>
);

// ─── File Upload Component ────────────────────────────────────────────────────

const FileUploadZone = ({ label, accept, onFile, file, uploading, uploaded, hint }) => {
  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    const f = e?.dataTransfer?.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e?.preventDefault()}
      className={`relative border-2 border-dashed rounded-xl p-5 text-center transition-all duration-200 cursor-pointer hover:border-brand-gold/60 hover:bg-brand-gold/5 ${
        uploaded ? 'border-green-400 bg-green-50' : 'border-border bg-surface/50'
      }`}
      onClick={() => document.getElementById(`file-${label}`)?.click()}
    >
      <input
        id={`file-${label}`}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onFile(e?.target?.files?.[0])}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          <span className="text-ios-caption1 text-text-secondary font-system">Téléchargement...</span>
        </div>
      ) : uploaded ? (
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={22} className="text-green-600" />
          </div>
          <span className="text-ios-caption1 text-green-700 font-system font-body-medium">
            {file?.name || 'Fichier téléchargé'}
          </span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center border border-border">
            <Icon name="Upload" size={20} className="text-text-secondary" />
          </div>
          <span className="text-ios-callout font-system font-body-medium text-text-primary">{label}</span>
          {hint && <span className="text-ios-caption1 text-text-secondary font-system">{hint}</span>}
        </div>
      )}
    </div>
  );
};

// ─── Certification Item ───────────────────────────────────────────────────────

const CertificationItem = ({ cert, onRemove }) => (
  <div className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border">
    <div className="w-9 h-9 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon name="Award" size={18} className="text-brand-earth" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-ios-callout font-system font-body-medium text-text-primary truncate">{cert?.name}</p>
      {cert?.issuer && (
        <p className="text-ios-caption1 text-text-secondary font-system truncate">{cert?.issuer}</p>
      )}
    </div>
    <button
      onClick={onRemove}
      className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors"
    >
      <Icon name="X" size={15} />
    </button>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const ProfessionalSignupPage = () => {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [applicationId, setApplicationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Email verification state
  const [awaitingEmailVerification, setAwaitingEmailVerification] = useState(false);
  const [verificationCheckLoading, setVerificationCheckLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  // Step 1 — Account
  const [account, setAccount] = useState({
    fullName: '',
    email: '',
    phone: '',
    whatsapp: '',
    password: '',
    confirmPassword: '',
  });

  // Step 2 — Identity / Credentials
  const [credentials, setCredentials] = useState({
    idDocumentType: 'national_id',
    licenseNumber: '',
    yearsExperience: '',
  });
  const [idFile, setIdFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [idUploaded, setIdUploaded] = useState(false);
  const [licenseUploaded, setLicenseUploaded] = useState(false);
  const [idUrl, setIdUrl] = useState('');
  const [licenseUrl, setLicenseUrl] = useState('');

  // Step 3 — Profile
  const [profile, setProfile] = useState({
    bio: '',
    city: 'Abidjan',
    commune: '',
    address: '',
    serviceZones: [],
    languages: ['Français'],
    websiteUrl: '',
    linkedinUrl: '',
    hourlyRate: '',
    dailyRate: '',
    availableDays: [],
    availableHoursStart: '08:00',
    availableHoursEnd: '18:00',
  });

  // Step 4 — Category & Certifications
  const [categoryData, setCategoryData] = useState({
    category: '',
    specialty: '',
    tags: [],
    tagInput: '',
  });
  const [certifications, setCertifications] = useState([]);
  const [certForm, setCertForm] = useState({ name: '', issuer: '', year: '' });
  const [certFile, setCertFile] = useState(null);
  const [certUploading, setCertUploading] = useState(false);
  const [certUploaded, setCertUploaded] = useState(false);

  // ─── Validation ─────────────────────────────────────────────────────────────

  const validateStep1 = () => {
    const e = {};
    if (!account?.fullName?.trim()) e.fullName = 'Nom complet requis';
    if (!account?.email?.trim()) e.email = 'Email requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(account?.email)) e.email = 'Email invalide';
    if (!account?.password) e.password = 'Mot de passe requis';
    else if (account?.password?.length < 8) e.password = 'Minimum 8 caractères';
    if (account?.password !== account?.confirmPassword) e.confirmPassword = 'Les mots de passe ne correspondent pas';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!idUploaded && !idUrl) e.idDocument = 'Pièce d\'identité requise';
    if (!credentials?.yearsExperience) e.yearsExperience = 'Années d\'expérience requises';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (!profile?.bio?.trim()) e.bio = 'Biographie requise';
    if (!profile?.commune) e.commune = 'Commune requise';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const validateStep4 = () => {
    const e = {};
    if (!categoryData?.category) e.category = 'Catégorie requise';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  // ─── Step Navigation ─────────────────────────────────────────────────────────

  const handleStep1Next = async () => {
    if (!validateStep1()) return;
    setLoading(true);
    try {
      // Create Supabase auth account
      const { data: authData, error: authErr } = await signUp(account?.email, account?.password, {
        full_name: account?.fullName,
        role: 'professional_applicant',
      });
      if (authErr) {
        setErrors({ submit: typeof authErr === 'string' ? authErr : authErr?.message || 'Erreur lors de la création du compte' });
        setLoading(false);
        return;
      }

      // Create draft application
      const { data: app, error: appErr } = await createApplication({
        fullName: account?.fullName,
        email: account?.email,
        phone: account?.phone,
        whatsapp: account?.whatsapp,
        category: '',
      });
      if (appErr) {
        setErrors({ submit: appErr });
        setLoading(false);
        return;
      }

      // Link user_id if available
      if (authData?.user?.id && app?.id) {
        await updateApplication(app?.id, { userId: authData?.user?.id });
      }

      setApplicationId(app?.id);

      // Send email verification link (expires in 24h)
      await sendEmailVerification(app?.id, account?.fullName, account?.email);

      // Show verification pending screen instead of advancing to step 2
      setAwaitingEmailVerification(true);
    } catch (err) {
      setErrors({ submit: err?.message || 'Une erreur est survenue' });
    }
    setLoading(false);
  };

  // Poll / manual check for email verification
  const handleCheckVerification = async () => {
    if (!applicationId) return;
    setVerificationCheckLoading(true);
    setVerificationError(null);
    const { verified, error } = await checkEmailVerified(applicationId);
    if (error) {
      setVerificationError('Erreur lors de la vérification. Réessayez.');
    } else if (verified) {
      setAwaitingEmailVerification(false);
      setStep(2);
    } else {
      setVerificationError('Email non encore confirmé. Vérifiez votre boîte mail et cliquez sur le lien.');
    }
    setVerificationCheckLoading(false);
  };

  const handleResendVerification = async () => {
    if (!applicationId) return;
    setVerificationCheckLoading(true);
    setVerificationError(null);
    const { success } = await sendEmailVerification(applicationId, account?.fullName, account?.email);
    if (success) {
      setVerificationError(null);
    } else {
      setVerificationError('Impossible de renvoyer l\'email. Réessayez dans quelques instants.');
    }
    setVerificationCheckLoading(false);
  };

  const handleIdFileUpload = async (file) => {
    if (!file || !applicationId) return;
    setIdFile(file);
    setIdUploading(true);
    const { url, error } = await uploadIdDocument(file, applicationId);
    if (!error && url) {
      setIdUrl(url);
      setIdUploaded(true);
      await updateApplication(applicationId, { idDocumentUrl: url, idDocumentType: credentials?.idDocumentType });
    }
    setIdUploading(false);
  };

  const handleLicenseFileUpload = async (file) => {
    if (!file || !applicationId) return;
    setLicenseFile(file);
    setLicenseUploading(true);
    const { url, error } = await uploadIdDocument(file, applicationId);
    if (!error && url) {
      setLicenseUrl(url);
      setLicenseUploaded(true);
      await updateApplication(applicationId, { professionalLicenseUrl: url });
    }
    setLicenseUploading(false);
  };

  const handleStep2Next = async () => {
    if (!validateStep2()) return;
    setLoading(true);
    await updateApplication(applicationId, {
      idDocumentUrl: idUrl,
      idDocumentType: credentials?.idDocumentType,
      professionalLicenseUrl: licenseUrl,
      licenseNumber: credentials?.licenseNumber,
      yearsExperience: credentials?.yearsExperience,
    });
    setLoading(false);
    setStep(3);
  };

  const handleStep3Next = async () => {
    if (!validateStep3()) return;
    setLoading(true);
    await updateApplication(applicationId, {
      bio: profile?.bio,
      city: profile?.city,
      commune: profile?.commune,
      address: profile?.address,
      serviceZones: profile?.serviceZones,
      languages: profile?.languages,
      websiteUrl: profile?.websiteUrl,
      linkedinUrl: profile?.linkedinUrl,
      hourlyRate: profile?.hourlyRate,
      dailyRate: profile?.dailyRate,
      availableDays: profile?.availableDays,
      availableHoursStart: profile?.availableHoursStart,
      availableHoursEnd: profile?.availableHoursEnd,
    });
    setLoading(false);
    setStep(4);
  };

  const handleCertFileUpload = async (file) => {
    if (!file || !applicationId) return;
    setCertFile(file);
    setCertUploading(true);
    const { url, error } = await uploadCertification(file, applicationId);
    if (!error && url) {
      setCertUploaded(true);
      setCertForm(f => ({ ...f, fileUrl: url }));
    }
    setCertUploading(false);
  };

  const handleAddCertification = () => {
    if (!certForm?.name?.trim()) return;
    const newCert = {
      name: certForm?.name,
      issuer: certForm?.issuer,
      year: certForm?.year,
      fileUrl: certForm?.fileUrl || null,
    };
    setCertifications(prev => [...prev, newCert]);
    setCertForm({ name: '', issuer: '', year: '' });
    setCertFile(null);
    setCertUploaded(false);
  };

  const handleAddTag = () => {
    const tag = categoryData?.tagInput?.trim();
    if (tag && !categoryData?.tags?.includes(tag)) {
      setCategoryData(d => ({ ...d, tags: [...d?.tags, tag], tagInput: '' }));
    }
  };

  const handleFinalSubmit = async () => {
    if (!validateStep4()) return;
    setLoading(true);
    try {
      await updateApplication(applicationId, {
        category: categoryData?.category,
        specialty: categoryData?.specialty,
        tags: categoryData?.tags,
        certifications,
      });
      await submitApplication(applicationId);
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err?.message || 'Erreur lors de la soumission' });
    }
    setLoading(false);
  };

  const toggleArrayItem = (arr, item, setter, key) => {
    setter(prev => ({
      ...prev,
      [key]: arr?.includes(item) ? arr?.filter(i => i !== item) : [...arr, item],
    }));
  };

  // ─── Email Verification Pending Screen ──────────────────────────────────────

  if (awaitingEmailVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-brand-gold/5 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Mail" size={44} className="text-brand-earth" />
          </div>
          <div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Confirmez votre email
            </h1>
            <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
              Un lien de confirmation a été envoyé à{' '}
              <strong className="text-text-primary">{account?.email}</strong>.
              Cliquez sur ce lien pour continuer votre inscription.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-border p-5 text-left space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Clock" size={16} className="text-amber-600" />
              </div>
              <div>
                <p className="text-ios-callout font-system font-body-semibold text-text-primary">Lien valable 24 heures</p>
                <p className="text-ios-caption1 text-text-secondary font-system">Après expiration, vous devrez recommencer l'inscription.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="ShieldCheck" size={16} className="text-blue-600" />
              </div>
              <div>
                <p className="text-ios-callout font-system font-body-semibold text-text-primary">Vérification requise</p>
                <p className="text-ios-caption1 text-text-secondary font-system">Votre profil ne peut pas être activé sans confirmation email.</p>
              </div>
            </div>
          </div>

          {verificationError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-left">
              <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-ios-caption1 font-system font-body-normal text-red-700">{verificationError}</p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={handleCheckVerification}
              disabled={verificationCheckLoading}
              className="w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-earth/90 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {verificationCheckLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Icon name="RefreshCw" size={16} /> J'ai confirmé mon email</>
              )}
            </button>
            <button
              onClick={handleResendVerification}
              disabled={verificationCheckLoading}
              className="w-full py-3 px-6 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium transition-all duration-200 hover:bg-surface/80 disabled:opacity-60"
            >
              Renvoyer le lien de confirmation
            </button>
          </div>

          <p className="text-ios-caption1 text-text-secondary font-system">
            Vérifiez également votre dossier spam si vous ne trouvez pas l'email.
          </p>
        </div>
      </div>
    );
  }

  // ─── Success Screen ──────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-brand-gold/5 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={44} className="text-green-600" />
          </div>
          <div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Candidature soumise !
            </h1>
            <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
              Votre dossier est en cours d'examen. Notre équipe vous contactera sous 48–72h pour valider votre profil.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-border p-5 text-left space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Mail" size={16} className="text-brand-earth" />
              </div>
              <div>
                <p className="text-ios-callout font-system font-body-semibold text-text-primary">Email de confirmation</p>
                <p className="text-ios-caption1 text-text-secondary font-system">Un email vous a été envoyé à {account?.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Clock" size={16} className="text-brand-earth" />
              </div>
              <div>
                <p className="text-ios-callout font-system font-body-semibold text-text-primary">Délai de traitement</p>
                <p className="text-ios-caption1 text-text-secondary font-system">48 à 72 heures ouvrables</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/professional-services-booking"
              className="w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold text-center transition-all duration-200 hover:bg-brand-earth/90"
            >
              Voir les professionnels
            </Link>
            <Link
              to="/homepage"
              className="w-full py-3 px-6 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium text-center transition-all duration-200 hover:bg-surface/80"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>Devenir Prestataire — Kapital States</title>
        <meta name="description" content="Inscrivez-vous comme prestataire de services sur Kapital States. Créez votre profil professionnel et rejoignez notre réseau." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-brand-gold/5">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-border/50 px-6 py-4 flex items-center justify-between">
          <Link to="/homepage" className="flex items-center space-x-2.5 group">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center">
              <Icon name="Building2" size={18} color="white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-display-semibold text-ios-callout text-brand-earth tracking-tight">
              Kapital States
            </span>
          </Link>
          <Link
            to="/auth"
            className="text-ios-callout font-system font-body-medium text-text-secondary hover:text-brand-earth transition-colors flex items-center gap-1.5"
          >
            <Icon name="LogIn" size={16} />
            Déjà inscrit ?
          </Link>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-10">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-gold/10 rounded-full mb-4">
              <Icon name="Briefcase" size={14} className="text-brand-earth" />
              <span className="text-ios-caption1 font-system font-body-semibold text-brand-earth uppercase tracking-widest">
                Espace Prestataires
              </span>
            </div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Rejoignez notre réseau
            </h1>
            <p className="text-ios-callout font-system font-body-normal text-text-secondary">
              Créez votre profil professionnel et accédez à des milliers de clients
            </p>
          </div>

          {/* Step Indicator */}
          <StepIndicator currentStep={step} />

          {/* Card */}
          <div className="bg-white rounded-2xl border border-border shadow-ios-light overflow-hidden">
            <div className="px-6 py-5 border-b border-border/50 bg-surface/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-brand-gold/10 rounded-xl flex items-center justify-center">
                  <Icon name={STEPS?.[step - 1]?.icon} size={18} className="text-brand-earth" />
                </div>
                <div>
                  <h2 className="text-ios-headline font-system font-body-semibold text-text-primary">
                    {step === 1 && 'Créer votre compte'}
                    {step === 2 && 'Vérification d\'identité'}
                    {step === 3 && 'Votre profil professionnel'}
                    {step === 4 && 'Catégorie & Certifications'}
                  </h2>
                  <p className="text-ios-caption1 text-text-secondary font-system">
                    Étape {step} sur {STEPS?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* ── STEP 1: Account ── */}
              {step === 1 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nom complet" required error={errors?.fullName}>
                      <TextInput
                        value={account?.fullName}
                        onChange={e => setAccount(a => ({ ...a, fullName: e?.target?.value }))}
                        placeholder="Jean Kouassi"
                      />
                    </Field>
                    <Field label="Email professionnel" required error={errors?.email}>
                      <TextInput
                        type="email"
                        value={account?.email}
                        onChange={e => setAccount(a => ({ ...a, email: e?.target?.value }))}
                        placeholder="jean@exemple.com"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Téléphone">
                      <TextInput
                        type="tel"
                        value={account?.phone}
                        onChange={e => setAccount(a => ({ ...a, phone: e?.target?.value }))}
                        placeholder="+225 07 00 00 00 00"
                      />
                    </Field>
                    <Field label="WhatsApp">
                      <TextInput
                        type="tel"
                        value={account?.whatsapp}
                        onChange={e => setAccount(a => ({ ...a, whatsapp: e?.target?.value }))}
                        placeholder="+225 07 00 00 00 00"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Mot de passe" required error={errors?.password}>
                      <TextInput
                        type="password"
                        value={account?.password}
                        onChange={e => setAccount(a => ({ ...a, password: e?.target?.value }))}
                        placeholder="Minimum 8 caractères"
                      />
                    </Field>
                    <Field label="Confirmer le mot de passe" required error={errors?.confirmPassword}>
                      <TextInput
                        type="password"
                        value={account?.confirmPassword}
                        onChange={e => setAccount(a => ({ ...a, confirmPassword: e?.target?.value }))}
                        placeholder="Répéter le mot de passe"
                      />
                    </Field>
                  </div>
                  {errors?.submit && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-ios-callout font-system font-body-normal text-red-700">{errors?.submit}</p>
                    </div>
                  )}
                  <div className="pt-2">
                    <button
                      onClick={handleStep1Next}
                      disabled={loading}
                      className="w-full py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-earth/90 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Continuer <Icon name="ArrowRight" size={17} /></>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 2: Identity ── */}
              {step === 2 && (
                <>
                  <Field label="Type de pièce d'identité">
                    <SelectInput
                      value={credentials?.idDocumentType}
                      onChange={e => setCredentials(c => ({ ...c, idDocumentType: e?.target?.value }))}
                      options={[
                        { value: 'national_id', label: 'Carte Nationale d\'Identité' },
                        { value: 'passport', label: 'Passeport' },
                        { value: 'residence_permit', label: 'Titre de séjour' },
                      ]}
                    />
                  </Field>

                  <Field label="Pièce d'identité (recto)" required error={errors?.idDocument}>
                    <FileUploadZone
                      label="Télécharger votre pièce d'identité"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onFile={handleIdFileUpload}
                      file={idFile}
                      uploading={idUploading}
                      uploaded={idUploaded}
                      hint="JPG, PNG ou PDF — max 20 Mo"
                    />
                  </Field>

                  <Field label="Licence / Autorisation professionnelle (optionnel)">
                    <FileUploadZone
                      label="Télécharger votre licence professionnelle"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onFile={handleLicenseFileUpload}
                      file={licenseFile}
                      uploading={licenseUploading}
                      uploaded={licenseUploaded}
                      hint="JPG, PNG ou PDF — max 20 Mo"
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Numéro de licence (optionnel)">
                      <TextInput
                        value={credentials?.licenseNumber}
                        onChange={e => setCredentials(c => ({ ...c, licenseNumber: e?.target?.value }))}
                        placeholder="Ex: LIC-2024-001"
                      />
                    </Field>
                    <Field label="Années d'expérience" required error={errors?.yearsExperience}>
                      <TextInput
                        type="number"
                        value={credentials?.yearsExperience}
                        onChange={e => setCredentials(c => ({ ...c, yearsExperience: e?.target?.value }))}
                        placeholder="Ex: 5"
                        min="0"
                        max="50"
                      />
                    </Field>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                    <Icon name="Info" size={17} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-ios-caption1 font-system font-body-normal text-blue-700 leading-relaxed">
                      Vos documents sont chiffrés et stockés de manière sécurisée. Ils ne seront utilisés que pour la vérification de votre identité.
                    </p>
                  </div>

                  {errors?.submit && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-ios-callout font-system text-red-700">{errors?.submit}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 py-3.5 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium transition-all duration-200 hover:bg-surface/80 flex items-center justify-center gap-2"
                    >
                      <Icon name="ArrowLeft" size={17} /> Retour
                    </button>
                    <button
                      onClick={handleStep2Next}
                      disabled={loading}
                      className="flex-1 py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-earth/90 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Continuer <Icon name="ArrowRight" size={17} /></>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 3: Profile ── */}
              {step === 3 && (
                <>
                  <Field label="Biographie professionnelle" required error={errors?.bio}>
                    <TextArea
                      value={profile?.bio}
                      onChange={e => setProfile(p => ({ ...p, bio: e?.target?.value }))}
                      placeholder="Décrivez votre expérience, vos compétences et ce qui vous distingue..."
                      rows={4}
                    />
                    <p className="text-ios-caption2 text-text-secondary font-system mt-1">{profile?.bio?.length || 0} caractères</p>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Ville">
                      <TextInput
                        value={profile?.city}
                        onChange={e => setProfile(p => ({ ...p, city: e?.target?.value }))}
                        placeholder="Abidjan"
                      />
                    </Field>
                    <Field label="Commune" required error={errors?.commune}>
                      <SelectInput
                        value={profile?.commune}
                        onChange={e => setProfile(p => ({ ...p, commune: e?.target?.value }))}
                        options={COMMUNES_ABIDJAN?.map(c => ({ value: c, label: c }))}
                        placeholder="Sélectionner une commune"
                      />
                    </Field>
                  </div>

                  <Field label="Adresse">
                    <TextInput
                      value={profile?.address}
                      onChange={e => setProfile(p => ({ ...p, address: e?.target?.value }))}
                      placeholder="Rue, quartier..."
                    />
                  </Field>

                  <Field label="Zones d'intervention">
                    <div className="flex flex-wrap gap-2">
                      {COMMUNES_ABIDJAN?.map(zone => (
                        <button
                          key={zone}
                          type="button"
                          onClick={() => toggleArrayItem(profile?.serviceZones, zone, setProfile, 'serviceZones')}
                          className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-system font-body-medium transition-all duration-200 ${
                            profile?.serviceZones?.includes(zone)
                              ? 'bg-brand-earth text-white' :'bg-surface border border-border text-text-secondary hover:border-brand-gold/50'
                          }`}
                        >
                          {zone}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Langues parlées">
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGES_OPTIONS?.map(lang => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => toggleArrayItem(profile?.languages, lang, setProfile, 'languages')}
                          className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-system font-body-medium transition-all duration-200 ${
                            profile?.languages?.includes(lang)
                              ? 'bg-brand-gold text-white' :'bg-surface border border-border text-text-secondary hover:border-brand-gold/50'
                          }`}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Tarif horaire (XOF)">
                      <TextInput
                        type="number"
                        value={profile?.hourlyRate}
                        onChange={e => setProfile(p => ({ ...p, hourlyRate: e?.target?.value }))}
                        placeholder="Ex: 15000"
                        min="0"
                      />
                    </Field>
                    <Field label="Tarif journalier (XOF)">
                      <TextInput
                        type="number"
                        value={profile?.dailyRate}
                        onChange={e => setProfile(p => ({ ...p, dailyRate: e?.target?.value }))}
                        placeholder="Ex: 80000"
                        min="0"
                      />
                    </Field>
                  </div>

                  <Field label="Jours disponibles">
                    <div className="flex flex-wrap gap-2">
                      {DAYS?.map(day => (
                        <button
                          key={day?.value}
                          type="button"
                          onClick={() => toggleArrayItem(profile?.availableDays, day?.value, setProfile, 'availableDays')}
                          className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-system font-body-medium transition-all duration-200 ${
                            profile?.availableDays?.includes(day?.value)
                              ? 'bg-brand-earth text-white' :'bg-surface border border-border text-text-secondary hover:border-brand-gold/50'
                          }`}
                        >
                          {day?.label}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Heure de début">
                      <TextInput
                        type="time"
                        value={profile?.availableHoursStart}
                        onChange={e => setProfile(p => ({ ...p, availableHoursStart: e?.target?.value }))}
                      />
                    </Field>
                    <Field label="Heure de fin">
                      <TextInput
                        type="time"
                        value={profile?.availableHoursEnd}
                        onChange={e => setProfile(p => ({ ...p, availableHoursEnd: e?.target?.value }))}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Site web (optionnel)">
                      <TextInput
                        type="url"
                        value={profile?.websiteUrl}
                        onChange={e => setProfile(p => ({ ...p, websiteUrl: e?.target?.value }))}
                        placeholder="https://monsite.com"
                      />
                    </Field>
                    <Field label="LinkedIn (optionnel)">
                      <TextInput
                        type="url"
                        value={profile?.linkedinUrl}
                        onChange={e => setProfile(p => ({ ...p, linkedinUrl: e?.target?.value }))}
                        placeholder="https://linkedin.com/in/..."
                      />
                    </Field>
                  </div>

                  {errors?.submit && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-ios-callout font-system text-red-700">{errors?.submit}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setStep(2)}
                      className="flex-1 py-3.5 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium transition-all duration-200 hover:bg-surface/80 flex items-center justify-center gap-2"
                    >
                      <Icon name="ArrowLeft" size={17} /> Retour
                    </button>
                    <button
                      onClick={handleStep3Next}
                      disabled={loading}
                      className="flex-1 py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-earth/90 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Continuer <Icon name="ArrowRight" size={17} /></>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 4: Category & Certifications ── */}
              {step === 4 && (
                <>
                  <Field label="Catégorie de service" required error={errors?.category}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {CATEGORIES?.map(cat => (
                        <button
                          key={cat?.value}
                          type="button"
                          onClick={() => setCategoryData(d => ({ ...d, category: cat?.value }))}
                          className={`px-3 py-3 rounded-xl text-ios-caption1 font-system font-body-medium transition-all duration-200 text-center ${
                            categoryData?.category === cat?.value
                              ? 'bg-brand-earth text-white shadow-ios-light'
                              : 'bg-surface border border-border text-text-secondary hover:border-brand-gold/50 hover:text-brand-earth'
                          }`}
                        >
                          {cat?.label}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Spécialité (optionnel)">
                    <TextInput
                      value={categoryData?.specialty}
                      onChange={e => setCategoryData(d => ({ ...d, specialty: e?.target?.value }))}
                      placeholder="Ex: Plomberie sanitaire, Architecture d'intérieur..."
                    />
                  </Field>

                  <Field label="Mots-clés / Tags">
                    <div className="flex gap-2">
                      <TextInput
                        value={categoryData?.tagInput}
                        onChange={e => setCategoryData(d => ({ ...d, tagInput: e?.target?.value }))}
                        placeholder="Ajouter un tag..."
                        onKeyDown={e => e?.key === 'Enter' && (e?.preventDefault(), handleAddTag())}
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-4 py-3 bg-surface border border-border rounded-xl text-text-secondary hover:text-brand-earth hover:border-brand-gold/50 transition-colors"
                      >
                        <Icon name="Plus" size={18} />
                      </button>
                    </div>
                    {categoryData?.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {categoryData?.tags?.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-gold/10 text-brand-earth rounded-lg text-ios-caption1 font-system font-body-medium"
                          >
                            {tag}
                            <button
                              onClick={() => setCategoryData(d => ({ ...d, tags: d?.tags?.filter(t => t !== tag) }))}
                              className="hover:text-red-500 transition-colors"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </Field>

                  {/* Certifications */}
                  <div className="space-y-3">
                    <label className="block text-ios-callout font-system font-body-medium text-text-primary">
                      Certifications & Diplômes
                    </label>

                    {certifications?.length > 0 && (
                      <div className="space-y-2">
                        {certifications?.map((cert, idx) => (
                          <CertificationItem
                            key={idx}
                            cert={cert}
                            onRemove={() => setCertifications(prev => prev?.filter((_, i) => i !== idx))}
                          />
                        ))}
                      </div>
                    )}

                    <div className="bg-surface/50 rounded-xl border border-border p-4 space-y-3">
                      <p className="text-ios-caption1 font-system font-body-semibold text-text-secondary uppercase tracking-widest">
                        Ajouter une certification
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextInput
                          value={certForm?.name}
                          onChange={e => setCertForm(f => ({ ...f, name: e?.target?.value }))}
                          placeholder="Nom de la certification *"
                        />
                        <TextInput
                          value={certForm?.issuer}
                          onChange={e => setCertForm(f => ({ ...f, issuer: e?.target?.value }))}
                          placeholder="Organisme émetteur"
                        />
                      </div>
                      <TextInput
                        value={certForm?.year}
                        onChange={e => setCertForm(f => ({ ...f, year: e?.target?.value }))}
                        placeholder="Année d'obtention (ex: 2022)"
                        type="number"
                        min="1970"
                        max="2030"
                      />
                      <FileUploadZone
                        label="Joindre le document (optionnel)"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onFile={handleCertFileUpload}
                        file={certFile}
                        uploading={certUploading}
                        uploaded={certUploaded}
                        hint="JPG, PNG ou PDF — max 20 Mo"
                      />
                      <button
                        type="button"
                        onClick={handleAddCertification}
                        disabled={!certForm?.name?.trim()}
                        className="w-full py-2.5 bg-brand-gold/10 border border-brand-gold/30 text-brand-earth rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-gold/20 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Icon name="Plus" size={16} /> Ajouter cette certification
                      </button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-4 space-y-2">
                    <p className="text-ios-caption1 font-system font-body-semibold text-brand-earth uppercase tracking-widest mb-3">
                      Récapitulatif
                    </p>
                    <div className="flex items-center gap-2 text-ios-callout font-system text-text-primary">
                      <Icon name="User" size={15} className="text-brand-earth flex-shrink-0" />
                      <span>{account?.fullName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ios-callout font-system text-text-primary">
                      <Icon name="Mail" size={15} className="text-brand-earth flex-shrink-0" />
                      <span>{account?.email}</span>
                    </div>
                    {categoryData?.category && (
                      <div className="flex items-center gap-2 text-ios-callout font-system text-text-primary">
                        <Icon name="Briefcase" size={15} className="text-brand-earth flex-shrink-0" />
                        <span>{CATEGORIES?.find(c => c?.value === categoryData?.category)?.label}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-ios-callout font-system text-text-primary">
                      <Icon name="Award" size={15} className="text-brand-earth flex-shrink-0" />
                      <span>{certifications?.length} certification(s)</span>
                    </div>
                  </div>

                  {errors?.submit && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-ios-callout font-system text-red-700">{errors?.submit}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setStep(3)}
                      className="flex-1 py-3.5 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium transition-all duration-200 hover:bg-surface/80 flex items-center justify-center gap-2"
                    >
                      <Icon name="ArrowLeft" size={17} /> Retour
                    </button>
                    <button
                      onClick={handleFinalSubmit}
                      disabled={loading}
                      className="flex-1 py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold transition-all duration-200 hover:bg-brand-earth/90 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Soumettre ma candidature <Icon name="Send" size={17} /></>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer note */}
          <p className="text-center text-ios-caption1 font-system font-body-normal text-text-secondary mt-6">
            En soumettant, vous acceptez nos{' '}
            <Link to="/legal-compliance-footer" className="text-brand-earth hover:underline">
              Conditions d'utilisation
            </Link>{' '}
            et notre{' '}
            <Link to="/legal-compliance-footer" className="text-brand-earth hover:underline">
              Politique de confidentialité
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default ProfessionalSignupPage;
